import {useState} from 'react'
import {downloadTemplateFile, TEMPLATE_TYPE} from '@/services/downloadTemplateFile'
import {downloadCSVFiles} from '@/helpers/shadcn/utils'
import {FileTransferButton} from '@/components/organisms/FileTransferButton'
import {useIntl} from 'react-intl'
import {UploadResult} from '@/components/organisms/FileUploadWithModal/FileUploadWithModal'
import {useActiveTiers} from '@/containers/ClubPlus/hooks/useActiveTiers'

export const useTierList = () => {
  const {tiers, invalidateTiers, isLoadingTiers} = useActiveTiers()
  const {formatMessage} = useIntl()

  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false)
  const [formData, setFormData] = useState({})

  const handleFileUpload = tierId => {
    // Implement the logic to handle file upload once the backend is ready
    console.log('handleFileUpload::tierId', tierId)
    setIsFileUploadModalOpen(true)
  }

  const handleFileDownload = async (tierId, tierName) => {
    const data = {tierId, tierName, type: TEMPLATE_TYPE.TIER_CUSTOMERS, addStores: false}
    const response = await downloadTemplateFile(data)
    if (!response) return
    const fileName = `ClubPlus_${tierName}`
    downloadCSVFiles(response, fileName)
  }

  const handleModalDismiss = uploadResult => {
    setIsFileUploadModalOpen(false)
    if (uploadResult === UploadResult.SUCCESS) {
      invalidateTiers()
    }
  }

  // Table column configuration
  const getColumns = () => [
    {
      name_of_db_column: 'name',
      header: formatMessage({id: 'CLUB_PLUS.TIER_LIST_TABLE_COLUMN_NAME'}),
    },
    {
      name_of_db_column: 'storesAssociated',
      header: formatMessage({id: 'CLUB_PLUS.TIER_LIST_TABLE_COLUMN_NUMBER_OF_STORES'}),
      cellData: ctx => {
        const storesAssociated = ctx?.row?.original?.storesAssociated
        const userLocale = navigator.language || 'en-US'
        return (
          <span style={{textAlign: 'right', fontWeight: 'bold', display: 'block', color: 'white'}}>
            {storesAssociated ? storesAssociated.toLocaleString(userLocale) : '-'}
          </span>
        )
      },
    },
    {
      name_of_db_column: 'activities',
      header: formatMessage({id: 'CLUB_PLUS.TIER_LIST_TABLE_COLUMN_ACTIVITIES_INCLUDED'}),
      cellData: ctx => {
        const activities = ctx?.row?.original?.activities
        const userLocale = navigator.language || 'en-US'
        return activities?.length ? (
          <ul className="list-disc space-y-1 pl-5">
            {activities.map(activity => (
              <li key={activity.activityID}>
                {activity.activityName} (
                {new Intl.NumberFormat(userLocale, {minimumFractionDigits: 1, maximumFractionDigits: 1}).format(
                  activity.multiplier
                )}
                x)
              </li>
            ))}
          </ul>
        ) : null
      },
    },
    {
      name_of_db_column: 'status',
      header: formatMessage({id: 'CLUB_PLUS.TIER_LIST_TABLE_COLUMN_STATUS'}),
      cellData: ctx => {
        const status = ctx?.row?.original?.status
        if (!status) return '-'
        return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
      },
    },
    {
      name_of_db_column: 'lastUpdated',
      header: formatMessage({id: 'CLUB_PLUS.TIER_LIST_TABLE_COLUMN_LAST_UPDATED'}),
      cellData: ctx => {
        const lastUpdated = ctx?.row?.original?.lastUpdated
        if (!lastUpdated) return '-'
        const date = new Date(lastUpdated)
        const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
        return formattedDate
      },
    },
    {
      name_of_db_column: 'uploadStatus',
      header: formatMessage({id: 'CLUB_PLUS.TIER_LIST_TABLE_COLUMN_UPLOAD_STATUS'}),
    },
    {
      columnKey: formatMessage({id: 'CLUB_PLUS.TIER_LIST_TABLE_COLUMN_UPLOAD'}),
      label_of_column: ' ',
      cellData: ctx => <FileTransferButton isUpload onClick={() => handleFileUpload(ctx?.row?.original?.tierID)} />,
    },
    {
      columnKey: formatMessage({id: 'CLUB_PLUS.TIER_LIST_TABLE_COLUMN_DOWNLOAD'}),
      label_of_column: ' ',
      cellData: ctx => (
        <FileTransferButton onClick={() => handleFileDownload(ctx?.row?.original?.tierID, ctx?.row?.original?.name)} />
      ),
    },
  ]

  return {
    tiers,
    isLoadingTiers,
    getColumns,
    isFileUploadModalOpen,
    setIsFileUploadModalOpen,
    handleModalDismiss,
    setFormData,
    formData,
  }
}
